import DeleteIcon from '@mui/icons-material/Delete'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import LockIcon from '@mui/icons-material/Lock'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import { Button, Divider, Grid, Stack, Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { UIAction, getEventSurveys } from 'app/slices/UI'
import DialogConfirm from 'components/Dialogs/DialogConfirm'
import DialogQR from 'components/Dialogs/DialogQR'
import EditInput from 'components/EditInput'
import EditAddress from 'components/Event/EditAddress'
import EditDateRange from 'components/Event/EditDateRange'
import EditSurvey from 'components/Event/EditSurvey'
import EditUseGPS from 'components/Event/EditUseGPS'
import EventTable from 'components/Event/Table'
import ShowAndReUploadImage from 'components/ShowAndReUploadImage'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'

export default function EventDetail() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { id } = useParams<{ id: string }>()
  const [releaseConfirm, setReleaseConfirm] = useState(false)
  const [unreleaseConfirm, setUnreleaseConfirm] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [showQR, setShowQR] = useState(false)
  const [loading, setLoading] = useState(false)
  const loginUser = useAppSelector((s) => s.UI.auth)
  const event = useAppSelector((s) =>
    s.Event.list.find((f) => f.id === Number(id))
  )
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (id) {
      dispatch(getEventSurveys(Number(id)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])
  useEffect(() => {
    if (id)
      dispatch(
        UIAction.setBreadcrumbs([
          {
            text: 'ホーム',
            url: '/',
          },
          {
            text: 'イベント一覧',
            url: '/event',
          },
          {
            text: 'イベント詳細',
            url: '/event/' + id,
          },
        ])
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])
  const releaseSubmit = async () => {
    setLoading(true)
    try {
      await axiosClient.put('/api/events/' + id, {
        data: JSON.stringify({ ['released']: true }),
      })
      const msg = 'イベントを公開しました。'
      enqueueSnackbar(msg, {
        variant: 'success',
      })
      setLoading(false)
      setReleaseConfirm(false)
    } catch (_error) {
      const msg = 'エラーが発生しました。'
      enqueueSnackbar(msg, {
        variant: 'error',
      })
    }
  }
  const unreleaseSubmit = async () => {
    setLoading(true)
    try {
      await axiosClient.put('/api/events/' + id, {
        data: JSON.stringify({ ['released']: false }),
      })
      const msg = 'イベントの公開を停止しました。'
      enqueueSnackbar(msg, {
        variant: 'success',
      })
      setLoading(false)
      setUnreleaseConfirm(false)
    } catch (_error) {
      const msg = 'エラーが発生しました。'
      enqueueSnackbar(msg, {
        variant: 'error',
      })
    }
  }
  const handleSubmit = async () => {
    try {
      setLoading(true)
      await axiosClient.delete('/api/events/' + id)
      const msg = 'イベントを削除しました。'
      enqueueSnackbar(msg, {
        variant: 'success',
      })
      setTimeout(() => {
        navigate('/event/')
      }, 100)
    } catch (error) {
      const msg = 'エラーが発生しました。'
      enqueueSnackbar(msg, {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }
  if (!event || !loginUser) return null
  return (
    <>
      <DialogQR
        open={showQR}
        url={`ev|${String(event.id)}`}
        header={<h4 className="text-center">{loginUser.id}</h4>}
        footer={<h4 className="text-center">{event.name}</h4>}
        handleClose={() => setShowQR(false)}
      />
      <DialogConfirm
        open={releaseConfirm}
        maxWidth="xs"
        loading={loading}
        handleSubmit={releaseSubmit}
        title="確認"
        primaryButtonText="公開"
        primaryButtonColor="primary"
        handleClose={() => setReleaseConfirm(false)}
      >
        <Typography>イベントを公開しますか？</Typography>
      </DialogConfirm>
      <DialogConfirm
        open={unreleaseConfirm}
        maxWidth="xs"
        loading={loading}
        handleSubmit={unreleaseSubmit}
        title="確認"
        primaryButtonText="非公開"
        primaryButtonColor="error"
        handleClose={() => setUnreleaseConfirm(false)}
      >
        <Typography>イベントの公開を停止しますか？</Typography>
      </DialogConfirm>
      <DialogConfirm
        open={deleteConfirm}
        maxWidth="xs"
        loading={loading}
        handleSubmit={handleSubmit}
        title="確認"
        primaryButtonText="削除"
        primaryButtonColor="error"
        handleClose={() => setDeleteConfirm(false)}
      >
        <Typography>イベントを削除しますか？</Typography>
      </DialogConfirm>
      <Grid container spacing={1} mt={2}>
        <Grid item xs={12}>
          <Typography
            variant="h5"
            component="h2"
            sx={{ borderBottom: 2 }}
            fontWeight="bold"
          >
            {event.name}
          </Typography>
        </Grid>
        <Grid item xs={12} ml={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Stack ml={1}>
                <EditInput
                  label="イベント名"
                  value={event.name}
                  apiEndpoint={'/api/events/' + id}
                  fieldName="name"
                />
                <EditAddress
                  label="開催場所"
                  prefectureId={event.prefectureId}
                  address={event.address}
                  apiEndpoint={'/api/events/' + id}
                />
                <EditDateRange
                  label="開催期間"
                  startDate={event.startDate}
                  endDate={event.endDate}
                  apiEndpoint={'/api/events/' + id}
                />

                <EditInput
                  label="概要説明文"
                  value={event.summary}
                  apiEndpoint={'/api/events/' + id}
                  fieldName="summary"
                />
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <ShowAndReUploadImage
                src={event.image}
                apiEndpoint={'/api/events/' + id}
                fieldName="image"
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <EditInput
                required
                label="クリア特典"
                value={event.rewardSummary}
                apiEndpoint={'/api/events/' + id}
                fieldName="rewardSummary"
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <ShowAndReUploadImage
                src={event.rewardImage}
                apiEndpoint={'/api/events/' + id}
                fieldName="rewardImage"
                canDelete
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <EditUseGPS
            label="GPS（位置情報）の使用"
            useGPS={event.useGPS}
            apiEndpoint={'/api/events/' + id + '/floors'}
            initFloors={event.floors}
          />
          <Divider sx={{ my: 2 }} />
          <EditSurvey
            label="アンケート項目選択"
            surveyIds={event.surveyIds
              .split('|')
              .sort()
              .map((v) => Number(v))}
              eventId={event.id}
            apiEndpoint={'/api/events/' + id}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Stack my={2}>
          {!event.released ? (
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="outlined"
                sx={{ my: 1 }}
                onClick={() => {
                  navigate('/event/' + id + '/add-point')
                }}
              >
                チェックポイント登録
              </Button>
            </Stack>
          ) : (
            ''
          )}
          <EventTable
            gps={event.useGPS}
            data={event.checkpoints}
            floor={event.floors}
          />
        </Stack>
      </Grid>
      <Grid
        item
        xs={12}
        ml={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Stack alignItems="center" justifyContent="center">
          <Button
            color="primary"
            variant="outlined"
            sx={{
              mt: 4,
              width: 200,
            }}
            onClick={() => navigate('/event/' + id + '/result')}
          >
            <Typography>イベント実績</Typography>
          </Button>
          <Button
            sx={{
              width: 200,
              mt: 4,
            }}
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => {
              setShowQR(true)
            }}
          >
            <Typography>二次元コード表示</Typography>
            <QrCode2Icon
              sx={{
                ml: 0.5,
              }}
            />
          </Button>
          <Button
            sx={{
              width: 200,
              mt: 4,
            }}
            size="small"
            color="primary"
            variant="contained"
            onClick={() => {
              event.released
                ? setUnreleaseConfirm(true)
                : setReleaseConfirm(true)
            }}
          >
            {event.released ? (
              <>
                <Typography>イベント非公開</Typography>
                <LockIcon
                  sx={{
                    ml: 0.5,
                  }}
                />
              </>
            ) : (
              <>
                <Typography>イベント公開</Typography>
                <LockOpenIcon
                  sx={{
                    ml: 0.5,
                  }}
                />
              </>
            )}
          </Button>
          {!event.released ? (
          <Button
            sx={{
              width: 200,
              mt: 4,
            }}
            size="small"
            color="error"
            variant="contained"
            onClick={() => {
              setDeleteConfirm(true)
            }}
          >
            <Typography>イベント削除</Typography>
            <DeleteIcon
              sx={{
                ml: 0.5,
              }}
            />
          </Button>
          ) : (
            ''
          )}
        </Stack>
      </Grid>
    </>
  )
}
